/* ========================================================================
   Build & Delivery‑Time Mapper – MULTILINGUAL EDITION
   ------------------------------------------------------------------------
   • Drop this file into  assets/build‑delivery‑mapper.js  (or similar) and
     include it in theme.liquid with:
       {{ 'build-delivery-mapper.js' | asset_url | script_tag }}
   • Requires the following globals (already present in your theme):
       – buildTimeData   (array of build‑time objects)
       – deliveryTimes   (object with { uk, us, eu, fr, ch, at, display_text })
       – product_js      (Shopify product JSON)
       – market_handle   (current market handle, e.g. 'uk', 'fr' …)
       – getSelectedOptions() ⇒ [{ value : 'string', … }, …]
   • Extending to new languages = edit the TRANSLATIONS object only.
   =======================================================================*/

/* -----------------------------------------------------------------------
   0 · TRANSLATIONS
   --------------------------------------------------------------------- */
/* -----------------------------------------------------------------------
   TRANSLATIONS  (now includes “types” synonyms for each language)
   --------------------------------------------------------------------- */
const TRANSLATIONS = {
  /* ---------- English ---------- */
  en: {
    set:       ['set'],
    stock:     ['stock'],
    noTable:   ['without table', 'no table'],
    noBench:   ['without bench', 'no bench'],
    wood: {
      walnut:  ['walnut'],
      oak:     ['oak'],
      ash:     ['ash']
    },
    tableWord: 'table',
    benchWord: 'bench',

    /* NEW — product-type synonyms */
    types: {
      table:      ['table'],
      bench:      ['bench'],
      chair:      ['chair'],
      extendable: ['extendable'],
      sofa:       ['sofa'],
      lounger:    ['lounger'],
      bed:        ['bed'],
      twoseater:  ['two seater', '2 seater', 'two-seater'],
      stool: ['stool'],
      laica: ['laica']
    }
  },

  /* ---------- German (EU / AT / CH) ---------- */
  eu: {
    set:       ['kollektion', 'satz'],
    stock:     ['lagerbestand', 'lager'],
    noTable:   ['ohne tisch', 'kein tisch'],
    noBench:   ['ohne bank', 'kein bank'],
    wood: {
      walnut:  ['nussbaum'],
      oak:     ['eiche'],
      ash:     ['esche']
    },
    tableWord: 'tisch',
    benchWord: 'bank',

    types: {
      table:      ['tisch'],
      bench:      ['bank'],
      chair:      ['stuhl'],
      extendable: ['ausziehbar', 'verlängerbar'],
      sofa:       ['sofa'],
      lounger:    ['liege', 'lounger'],
      bed:        ['bett'],
      twoseater:  ['zweisitzer', '2-sitzer'],
      stool: ['stool'],
      laica: ['laica']
    }
  },

  /* ---------- French ---------- */
  fr: {
    set:       ['collection'],
    stock:     ['stock'],
    noTable:   ['sans table'],
    noBench:   ['sans banc'],
    wood: {
      walnut:  ['noyer'],
      oak:     ['chêne'],
      ash:     ['frêne', 'fumé']
    },
    tableWord: 'table',
    benchWord: 'banc',

    types: {
      table:      ['table'],
      bench:      ['banc'],
      chair:      ['chaise'],
      extendable: ['extensible', 'à rallonge'],
      sofa:       ['canapé'],
      lounger:    ['chaise longue', 'lounger'],
      bed:        ['lit'],
      twoseater:  ['deux places', '2 places'],
      stool: ['stool'],
      laica: ['laica']
    }
  }
};


/* -----------------------------------------------------------------------
   1 · MARKET → LANGUAGE mapping
   --------------------------------------------------------------------- */
const REGION_TO_LANG = {
  gb: 'en', uk: 'en', us: 'en',
  eu: 'eu', at: 'eu', ch: 'eu', swiss: 'eu', de: 'eu',
  fr: 'fr'
  
};

const MARKET_TO_REGION = {
  gb: 'uk', us: 'us',
  eu: 'eu', at: 'at', ch: 'ch', swiss: 'ch', de: 'eu',
  fr: 'fr'
  
};





function detectSelectedComponents(product_js, getSelectedOptions) {
  if (!product_js || !Array.isArray(product_js.options) || typeof getSelectedOptions !== 'function') {
    console.error('Required data missing');
    return null;
  }

  const selected = getSelectedOptions(); // e.g., [{ optionNumber: "option1", value: "Without Bench" }, ...]
  const options  = product_js.options;   // e.g., ["Table", "Bench"]

  const langKey = REGION_TO_LANG[(market_handle ?? 'uk').toLowerCase()] || 'en';
  const T = TRANSLATIONS[langKey] || TRANSLATIONS.en;

  const tableLabel = T.tableWord.toLowerCase();  // e.g., "table" / "tisch"
  const benchLabel = T.benchWord.toLowerCase();  // e.g., "bench" / "bank"

  let hasTable = false;
  let hasBench = false;

  for (const sel of selected) {
    const optIndex = parseInt(sel.optionNumber.replace('option', ''), 10) - 1;
    const optName  = (options[optIndex] || '').toLowerCase();  // from product_js.options
    const val      = (sel.value || '').toLowerCase();          // current selection value

    // Only count if NOT a "without" selection
    const notWithout = !(
      (optName.includes(tableLabel) && T.noTable.some(nt => val.includes(nt))) ||
      (optName.includes(benchLabel) && T.noBench.some(nb => val.includes(nb)))
    );

    if (optName.includes(tableLabel) && notWithout) hasTable = true;
    if (optName.includes(benchLabel) && notWithout) hasBench = true;
  }

  return {
    hasTable,
    hasBench,
    selected: hasTable && hasBench
      ? 'both'
      : hasTable
      ? 'table'
      : hasBench
      ? 'bench'
      : 'none'
  };
}

/* ------------------------------------------------------------------
   Utility: strip accents / umlauts so comparisons are ASCII–safe
------------------------------------------------------------------ */
const deburr = str =>
  str
    .toLowerCase()
    .normalize('NFD')           // ü → u +  ̈
    .replace(/[\u0300-\u036f]/g, ''); // drop all “combining” marks

/* -----------------------------------------------------------
   filterCandidatesByProductKind
   -----------------------------------------------------------
   • If the product title (any language) contains a table word,
     keep only rows whose tags also contain a table synonym.
   • Same for bench. If the title has neither word, the function
     returns the list untouched.
----------------------------------------------------------- */
function filterCandidatesByProductKind(candidates, title) {
  /* single cached arrays of every translated word */
  const TABLE_WORDS = window._TABLE_WORDS_ ||
    (window._TABLE_WORDS_ = Array.from(
      new Set(Object.values(TRANSLATIONS).map(l => l.tableWord.toLowerCase()))
    ));

  const BENCH_WORDS = window._BENCH_WORDS_ ||
    (window._BENCH_WORDS_ = Array.from(
      new Set(Object.values(TRANSLATIONS).map(l => l.benchWord.toLowerCase()))
    ));

  const isTableProduct = TABLE_WORDS.some(w => title.includes(w));
  const isBenchProduct = BENCH_WORDS.some(w => title.includes(w));

  /* nothing to do → give back original list */
  if (!isTableProduct && !isBenchProduct) return candidates;

  return candidates.filter(row => {
    const tagSet = row.tags.map(t => String(t).toLowerCase());
    if (isTableProduct) return tagSet.some(t => TABLE_WORDS.includes(t));
    if (isBenchProduct) return tagSet.some(t => BENCH_WORDS.includes(t));
    return true;                       // fallback (shouldn’t run)
  });
}



/* ------------------------------------------------------------------
   1. findDesignInTitle
   - returns the FIRST design string (original casing) whose
     deburred form is found in the deburred product title
------------------------------------------------------------------ */
function findDesignInTitle(cands, title) {
  const needle = deburr(title);

  for (const cand of cands) {
    const list = Array.isArray(cand.designs) ? cand.designs : [];

    for (const design of list) {
      if (needle.includes(deburr(design))) {
        
        return design;      // return the original, un-modified string
      }
    }
  }
  return null;
}
/* ------------------------------------------------------------------
   2. filterByDesign
   - if design == null  → keep only rows that have NO designs listed
   - otherwise          → keep rows whose deburred design list
                          contains the deburred “design” argument
------------------------------------------------------------------ */
function filterByDesign(cands, design) {
  if (!design) return cands.filter(c => !(c.designs && c.designs.length));

  const wanted = deburr(design);
  return cands.filter(c =>
    (c.designs || []).some(d => deburr(d) === wanted)
  );
}

/* ---------- product-kind filter (table / bench words in title) ---------- */
function filterByKind(cands, title) {
  const TABLES = window._TABLES_ ||= [...new Set(Object.values(TRANSLATIONS).map(l => l.tableWord.toLowerCase()))];
  const BENCHS = window._BENCHS_ ||= [...new Set(Object.values(TRANSLATIONS).map(l => l.benchWord.toLowerCase()))];
  const wantsTable = TABLES.some(w => title.includes(w));
  const wantsBench = BENCHS.some(w => title.includes(w));
  if (!wantsTable && !wantsBench) return cands;

  return cands.filter(r => {
    const ts = (r.types || []).map(t => t.toLowerCase());
    return wantsTable ? ts.includes('table') : ts.includes('bench');
  });
}

/* -----------------------------------------------------------
   filterByType – multi-language type resolver
   -----------------------------------------------------------
   1.  “needed”  → a string or array of strings taken from the
       product title / option picker (may be translated).
   2.  Each candidate row has  candidate.types  containing the
       canonical English keys.
   3.  Function returns rows whose canonical types overlap with
       the canonicalised “needed” list (OR logic).
----------------------------------------------------------- */
function filterByType(cands, needed) {
  if (!needed) return cands;

  /* --- 1 · build translation → canonical map ONCE --- */
  if (!window._TYPE_CANON_MAP_) {
    const canonicalKeys = [
      'table', 'bench', 'chair', 'extendable',
      'sofa', 'lounger', 'bed', 'twoseater'
    ];

    const map = Object.create(null);
    for (const canon of canonicalKeys) map[canon] = canon;   // self-map

    // gather synonyms from every language block (if provided)
    for (const lang of Object.values(TRANSLATIONS)) {
      if (!lang.types) continue;  // optional “types” sub-object

      for (const [canon, synonyms] of Object.entries(lang.types)) {
        const key = canon.toLowerCase();
        (Array.isArray(synonyms) ? synonyms : [synonyms]).forEach(s => {
          map[s.toLowerCase()] = key;
        });
      }
    }
    window._TYPE_CANON_MAP_ = map;           // cache
  }
  const TYPE_MAP = window._TYPE_CANON_MAP_;

  /* --- 2 · canonicalise the request list --- */
  const needCanon = (Array.isArray(needed) ? needed : [needed])
                    .map(x => TYPE_MAP[x.toLowerCase()])
                    .filter(Boolean);       // drop unknown words

  if (!needCanon.length) return cands;       // nothing recognised

  /* --- 3 · filter rows by overlap (OR logic) --- */
  return cands.filter(row => {
    const rowCanon = (row.types || []).map(t => String(t).toLowerCase());
    return needCanon.some(n => rowCanon.includes(n));
  });
}


/* ---------- detect table / bench selection (sets) ---------- */
function detectSelectedComponents(product_js, getSelectedOptions) {
  if (!product_js || !Array.isArray(product_js.options) || typeof getSelectedOptions !== 'function')
    return { selected: 'none' };

  const langKey = REGION_TO_LANG[(market_handle ?? 'uk').toLowerCase()] || 'en';
  const T       = TRANSLATIONS[langKey] || TRANSLATIONS.en;

  const opts    = product_js.options.map(o => o.toLowerCase());
  const sel     = getSelectedOptions();

  let hasTable = false, hasBench = false;
  for (const s of sel) {
    const idx   = parseInt(s.optionNumber.replace('option',''),10)-1;
    const label = opts[idx] || '';
    const val   = (s.value||'').toLowerCase();

    if (label.includes(T.tableWord) && !T.noTable.some(x=>val.includes(x))) hasTable = true;
    if (label.includes(T.benchWord) && !T.noBench.some(x=>val.includes(x))) hasBench = true;
  }
  return {
    selected: hasTable && hasBench ? 'both' : hasTable ? 'table' : hasBench ? 'bench' : 'none'
  };
}







/* -----------------------------------------------------------------------
   2 · Helper: week‑number formatter
   --------------------------------------------------------------------- */
function formatWeeks(n) {
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  const lo  = Math.floor(num);
  return (num - lo) >= 0.5 ? `${lo}-${lo + 1}` : String(lo);
}

/* -----------------------------------------------------------------------
   3 · MAIN: mapProductToBuildData()
   --------------------------------------------------------------------- */
function mapProductToBuildData() {
  
  /* guards */
  if (typeof buildTimeData === 'undefined' || typeof deliveryTimes === 'undefined' ||
      typeof product_js    === 'undefined' || typeof getSelectedOptions !== 'function') {
    console.error('mapProductToBuildData(): globals missing'); return null;
  }

  /* context */
  const region  = (market_handle ?? 'uk').toLowerCase();
  const langKey = REGION_TO_LANG[region] || 'en';
  const T       = TRANSLATIONS[langKey]  || TRANSLATIONS.en;
  const regKey  = MARKET_TO_REGION[region] || region;

  const title = (product_js.title ?? '').toLowerCase();
  if (T.stock.some(w => title.includes(w))) return null;

 /* -----------------------------------------------------------
   1 · material filter  (title + product_material, diacritic-safe)
----------------------------------------------------------- */

const optionWords = (getSelectedOptions() || []).map(o => deburr(o.value));

const matSrc = [
  deburr(title),
  deburr(product_material),
  ...optionWords
].join(' ');

/* walnut vs oak/ash bucket */
const isWalnut = T.wood.walnut
  .map(w => deburr(w))
  .some(w => matSrc.includes(w));

const wood = isWalnut ? 'walnut' : 'oak_ash';
/* -----------------------------------------------------------
   2 · Candidate filter (category + material + options)
----------------------------------------------------------- */
/* filter candidate rows by wood bucket */
/* ---------- material filter (category + material array) ---------- */
const isWalnutWanted = wood === 'walnut';

let cands = buildTimeData.filter(r => {
  const cat  = deburr(r.category);
  const mats = Array.isArray(r.material) ? r.material : [r.material];
  const matMatch = mats
    .map(m => deburr(m))
    .some(m => isWalnutWanted ? m.includes('walnut')
                              : m.includes('oak') || m.includes('ash'));

  if (isWalnutWanted) {
    return cat.includes('walnut') || matMatch;
  }
  return cat.includes('oak') || cat.includes('ash') || matMatch;
});

  /* 2 · design filter */
  const design = findDesignInTitle(cands, title);
  cands = filterByDesign(cands, design);
  if (!cands.length) return null;

  /* 3 · kind filter (word “table” / “bench” in title) */
  cands = filterByKind(cands, title);
  if (!cands.length) return null;

  /* 4 · type filter */
  const isSet = T.set.some(w => title.includes(w));
  /* ---------- build needType list ---------- */
let needType = null;

if (isSet) {
  const comp = detectSelectedComponents(product_js, getSelectedOptions);
  if (comp.selected === 'table')       needType = 'table';
  else if (comp.selected === 'bench')  needType = 'bench';
  else if (comp.selected === 'both')   needType = ['table', 'bench'];
} else {
  /* gather every synonym → canonical key once per language */
  if (!window._TYPE_SYNONYM_MAP_) {
    const map = Object.create(null);
    for (const [lang, data] of Object.entries(TRANSLATIONS)) {
      if (!data.types) continue;
      for (const [canon, syns] of Object.entries(data.types)) {
        (Array.isArray(syns) ? syns : [syns]).forEach(s => {
          map[s.toLowerCase()] = canon.toLowerCase();
        });
      }
    }
    window._TYPE_SYNONYM_MAP_ = map;
  }
  const TYPE_MAP = window._TYPE_SYNONYM_MAP_;

  const sources = [deburr(title), deburr(product_js.type || '')];

  const found = new Set();
  for (const src of sources) {
    for (const [syn, canon] of Object.entries(TYPE_MAP)) {
      if (src.includes(syn)) found.add(canon);
    }
  }

  if (found.size) {
    needType = [...found];          // array or single element
    if (needType.length === 1) needType = needType[0];
  }
}

/* ---------- type filter (OR logic) ---------- */
cands = filterByType(cands, needType);
if (!cands.length) return null;

  /* 5 · pick candidate with larger build time */
  const best = cands.reduce((a,b)=>
    (Number(b[`build_${regKey}`])||0) > (Number(a[`build_${regKey}`])||0) ? b : a
  );
  if (!best) return null;

  /* payload */
  const build = formatWeeks(best[`build_${regKey}`]);
  const ship  = formatWeeks(deliveryTimes[regKey]);

  const display = (deliveryTimes.display_text ?? '')
                    .replace(/{{\s*b_time\s*}}/gi, build)
                    .replace(/{{\s*d_time\s*}}/gi, ship);

  return {
    buildTime:           build,
    deliveryTime:        ship,
    displayText:         display,
    matchedObjectHandle: best.handle,
    matchedTypes:        best.types,
    matchedDesigns:      best.designs
  };
}















/* ============================================================
   Delivery‑banner updater – DE / EN plural + bold labels
   ============================================================ */

let originalDeliveryHTML = null;
const DELIVERY_SELECTOR  = '.delivery_time';

/* --- decode &lt; / &gt; etc. ---------------------------------- */
const decodeHTML = str => {
  const t = document.createElement('textarea');
  t.innerHTML = str;
  return t.value;
};

/* --- singular ↔ plural helper ------------------------------ */
function pluraliseWeeks(text, buildStr, delStr) {
  let out = text;
  const swap = (num, plural, singular) => {
    if (num === '1') {
      out = out.replace(new RegExp(`${num}\\s+${plural}`, 'i'),
                        `${num} ${singular}`);
    }
  };
  if (/wochen/i.test(out)) { swap(buildStr, 'WOCHEN', 'WOCHE'); swap(delStr,'WOCHEN','WOCHE'); }
  if (/weeks/i.test(out))  { swap(buildStr, 'weeks',  'week');  swap(delStr, 'weeks', 'week'); }
  return out;
}

/* --- main refresh ----------------------------------------- */
function update_build_delivery_text_commulative() {
  const el = document.querySelector(DELIVERY_SELECTOR);
  if (!el) return;

  if (originalDeliveryHTML === null) originalDeliveryHTML = el.innerHTML;

  const res = typeof mapProductToBuildData === 'function' ? mapProductToBuildData() : null;

  console.log("Matched MetaObject:", res);


 // ⏱️ Compute total weeks
    const build = parseFloat(res.buildTime);
    const delivery = parseFloat(res.deliveryTime);
    const total = build + delivery;

    const region = (market_handle ?? 'uk').toLowerCase();
    const langKey = REGION_TO_LANG[region] || 'en';

    let totalTimeStr = '';
    if (langKey === 'fr') {
      totalTimeStr = total === 1 ? '1 semaine' : `${total} semaines`;
    } else if (langKey === 'eu') {
      totalTimeStr = total === 1 ? '1 Woche' : `${total} Wochen`;
    } else {
      totalTimeStr = total === 1 ? '1 week' : `${total} weeks`;
    }

    el.children[0].firstChild.textContent = totalTimeStr;
  
  let html = originalDeliveryHTML;

  if (res && res.displayText) {
    let txt = decodeHTML(res.displayText);                   // un‑escape tags
    txt     = pluraliseWeeks(txt, res.buildTime, res.deliveryTime);
    html    = `<p class="delivery-banner-text">${txt}</p>`;
  }

  el.children[1].children[0].innerHTML = html;
  document.querySelector('.shipping-delivery')?.style.setProperty('display', 'flex', 'important');


  // if (el.innerHTML !== html) el.innerHTML = html;
}
function update_build_delivery_text() {
  const el = document.querySelector(DELIVERY_SELECTOR);
  if (!el) return;

  if (originalDeliveryHTML === null) originalDeliveryHTML = el.innerHTML;

  const res = typeof mapProductToBuildData === 'function' ? mapProductToBuildData() : null;

  console.log("Matched MetaObject:", res);
  let html = originalDeliveryHTML;

  if (res && res.displayText) {
    let txt = decodeHTML(res.displayText);                   // un‑escape tags
    txt     = pluraliseWeeks(txt, res.buildTime, res.deliveryTime);
    html    = `<p class="delivery-banner-text">${txt}</p>`;
  }

  if (el.innerHTML !== html) el.innerHTML = html;
  document.getElementById("shipping-delivery-display").style.display = "flex";
}

// /* run once */
// if (document.readyState === 'loading') {
//   document.addEventListener('DOMContentLoaded', update_build_delivery_text);
// } else {
//   update_build_delivery_text();
// }

/* expose */
window.update_build_delivery_text = update_build_delivery_text;
